import React from "react";
import { useTranslation } from "react-i18next";
import type { DeviceConnection } from "../../../api/deviceConnection";
import { AllDataDevice } from "../../../generated/types";
import { EntityCard } from "../../common/EntityCard";
import {
  connectionDotClass,
  connectionLabel,
  isRemoteBlocked,
} from "./ConnectionDot";
import { DeviceTypeBadge } from "./DeviceTypeBadge";

type DeviceMobileCardProps = {
  data: AllDataDevice;
  isFavorite: boolean;
  onToggleFavorite: (e: React.MouseEvent) => void;
  onView?: (e: React.MouseEvent) => void;
  /** Remplace élevage/bâtiment par l'UUID, hors espace admin (cf. DeviceRow). */
  showUuid?: boolean;
  onSelect?: () => void;
  /** État de connexion VPN de la box (cf. DeviceRow). */
  connection?: DeviceConnection;
};

export const DeviceMobileCard = ({
  data,
  isFavorite,
  onToggleFavorite,
  onView,
  onSelect,
  showUuid = false,
  connection,
}: DeviceMobileCardProps) => {
  const { t } = useTranslation();
  if (!data?.device) return null;
  const remoteBlocked = isRemoteBlocked(connection);

  return (
    <EntityCard
      title={data.device.name || data.device.uuid}
      statusDotClassName={connectionDotClass(connection?.status)}
      statusTitle={connectionLabel(t, connection)}
      viewDisabled={remoteBlocked}
      viewTitle={
        remoteBlocked
          ? t("remote-view-blocked-disconnected")
          : t("remote-view-open")
      }
      isFavorite={isFavorite}
      onToggleFavorite={onToggleFavorite}
      onView={onView}
      onClick={onSelect}
      rows={[
        {
          label: t("device-type"),
          value: <DeviceTypeBadge type={data.device.type} />,
        },
        {
          label: t("section-company"),
          value: data.company?.company_name || "-",
        },
        // Hors espace admin, élevage et bâtiment sont vides (route scopée) : on affiche
        // l'UUID à la place, qui est l'identifiant réellement manipulé.
        ...(showUuid
          ? [{ label: t("device-uuid"), value: data.device.uuid || "-" }]
          : [
              {
                label: t("section-breeding"),
                value: data.breeding?.name || "-",
              },
              {
                label: t("section-building"),
                value: data.building?.name || "-",
              },
            ]),
      ]}
    />
  );
};
